import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Calculate,
  Save,
  TrendingUp,
  AttachMoney,
  Analytics
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import axios from 'axios';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const CostCalculation = () => {
  // State for saved estimations (predictions)
  const [savedEstimations, setSavedEstimations] = useState([]);
  const [savedCostEstimations, setSavedCostEstimations] = useState([]);
  
  // State for selected prediction and product
  const [selectedPredictionId, setSelectedPredictionId] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [currentPrice, setCurrentPrice] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // State for cost estimation
  const [totalEstimation, setTotalEstimation] = useState(0);
  
  // State for profit calculation
  const [selectedSavedEstimationId, setSelectedSavedEstimationId] = useState('');
  const [amountTaken, setAmountTaken] = useState(0);
  const [profitChartData, setProfitChartData] = useState(null);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Function to load predictions from backend
  const loadPredictions = async () => {
    try {
              const response = await axios.get('https://stu150255.webhosting.arden.ac.uk/COM6008/api/get-predictions/');
      console.log('Predictions:', response.data.predictions);
      setSavedEstimations(response.data.predictions);
    } catch (err) {
      console.error('Error fetching predictions:', err);
      setError('Failed to load predictions from backend.');
    }
  };

  // Function to load cost estimations from backend
  const loadEstimations = async () => {
    try {
              const response = await axios.get('https://stu150255.webhosting.arden.ac.uk/COM6008/api/estimations/');
      console.log('Estimations:', response.data.estimations);
      setSavedCostEstimations(response.data.estimations);
    } catch (err) {
      console.error('Error fetching estimations:', err);
      setError('Failed to load estimations from backend.');
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadPredictions();
    loadEstimations();
  }, []);

  // Prepare profit chart data when estimations change
  useEffect(() => {
    if (savedCostEstimations.length > 0) {
      const sortedEstimations = [...savedCostEstimations].sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );

      const labels = sortedEstimations.map(
        (item) => `ID: ${item.id} (${new Date(item.timestamp).toLocaleDateString()})`
      );

      const profits = sortedEstimations.map((item) => {
        const profit = (item.amount_taken ?? 0) - item.total_estimation;
        return profit;
      });

      setProfitChartData({
        labels: labels,
        datasets: [
          {
            label: 'Profit Over Time',
            data: profits,
            fill: false,
            borderColor: 'rgb(75, 192, 192)',
            backgroundColor: 'rgba(75, 192, 192, 0.2)',
            tension: 0.1,
          },
        ],
      });
    }
  }, [savedCostEstimations]);

  // Handler to select a prediction
  const handlePredictionChange = (predictionId) => {
    setSelectedPredictionId(predictionId);
    setSelectedProduct('');
    setCurrentPrice('');
  };

  // Modal handlers
  const handleOpenModal = (item) => {
    setSelectedProduct(item);
    setCurrentPrice(localStorage.getItem(item) ?? '');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedProduct('');
    setCurrentPrice('');
    setIsModalOpen(false);
  };

  // Handler to update price
  const handleUpdatePrice = () => {
    if (selectedProduct && currentPrice) {
      localStorage.setItem(selectedProduct, currentPrice);
      alert(`Price for ${selectedProduct} updated to PKR ${currentPrice}`);
      handleCloseModal();
    } else {
      alert('Please enter a valid price.');
    }
  };

  // Handler to calculate cost estimation
  const handleCalculateEstimation = () => {
    const prediction = savedEstimations.find(
      (est) => est.id === selectedPredictionId
    );
    
    if (!prediction) {
      setError('Please select a prediction first');
      return;
    }

    let cost = 0;
    for (const item in prediction.menu_prices) {
      const price = Number(localStorage.getItem(item) ?? 0);
      const quantity = Number(prediction.menu_items[item]);
      cost += price * quantity;
    }

    setTotalEstimation(Math.floor(cost));
    setError('');
  };

  // Handler to save the current estimation
  const handleSaveEstimation = async () => {
    if (selectedPredictionId === '') {
      setError('Please select a prediction first');
      return;
    }

    setLoading(true);
    try {
              await axios.post('https://stu150255.webhosting.arden.ac.uk/COM6008/api/estimations/', {
        total_estimation: totalEstimation,
        prediction_id: selectedPredictionId,
      });

      await loadEstimations();
      alert('Estimation saved successfully!');
      setError('');
    } catch (err) {
      console.error('Save estimation error:', err);
      setError(err.response?.data?.error || 'Failed to save estimation');
    } finally {
      setLoading(false);
    }
  };

  // Handler to save analytics
  const handleSaveAnalytics = async () => {
    if (selectedSavedEstimationId === '') {
      setError('Please select an estimation first');
      return;
    }

    setLoading(true);
    try {
              const response = await axios.put('https://stu150255.webhosting.arden.ac.uk/COM6008/api/estimations/', {
        estimation_id: selectedSavedEstimationId,
        amount_taken: amountTaken,
      });
      
      setSavedCostEstimations(response.data.estimations);
      alert('Analytics saved to database');
      setError('');
    } catch (err) {
      console.error('Save analytics error:', err);
      setError(err.response?.data?.error || 'Failed to save analytics');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #ff6b6b, #feca57)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 4
          }}
        >
          <Calculate sx={{ mr: 2, fontSize: 'inherit', color: '#ff6b6b' }} />
          Cost Calculation (Predictive)
        </Typography>
      </motion.div>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Section 1: Price of Products */}
        <Grid item xs={12}>
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <Card>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  <AttachMoney sx={{ mr: 1 }} />
                  Price of Products
                </Typography>

                {/* Dropdown to select saved prediction */}
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Select Saved Prediction</InputLabel>
                  <Select
                    value={selectedPredictionId}
                    onChange={(e) => handlePredictionChange(e.target.value)}
                    label="Select Saved Prediction"
                  >
                    {savedEstimations.map((est) => (
                      <MenuItem key={est.id} value={est.id}>
                        Prediction ID: {est.id} | Menu: {est.menu_name.charAt(0).toUpperCase() + est.menu_name.slice(1)} | 
                        Guests: {est.guests} | Location: {est.location.charAt(0).toUpperCase() + est.location.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Display list of products with current prices */}
                {selectedPredictionId !== '' && (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Product</strong></TableCell>
                          <TableCell><strong>Price per Amount</strong></TableCell>
                          <TableCell><strong>Amount</strong></TableCell>
                          <TableCell><strong>Total Price</strong></TableCell>
                          <TableCell><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {savedEstimations
                          .filter((est) => est.id === selectedPredictionId)
                          .map((est) =>
                            Object.entries(est.menu_items).map(([item, amount]) => (
                              <TableRow key={item}>
                                <TableCell>{item}</TableCell>
                                <TableCell>PKR {localStorage.getItem(item) ?? 0}</TableCell>
                                <TableCell>
                                  {amount} {item === 'Naan' || item === 'Salad' ? 'units' : 'kgs'}
                                </TableCell>
                                <TableCell>
                                  PKR {(parseInt(localStorage.getItem(item) ?? '0') * amount).toLocaleString()}
                                </TableCell>
                                <TableCell>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => handleOpenModal(item)}
                                  >
                                    Update Price
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Section 2: Cost Estimation */}
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <Card>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  <TrendingUp sx={{ mr: 1 }} />
                  Cost Estimation
                </Typography>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleCalculateEstimation}
                  sx={{
                    mb: 3,
                    background: 'linear-gradient(45deg, #667eea, #764ba2)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #5a6fd8, #6a4190)',
                    }
                  }}
                >
                  Calculate Cost Estimation
                </Button>

                <Typography variant="h6" gutterBottom>
                  Total Estimation: PKR {totalEstimation.toLocaleString()}
                </Typography>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSaveEstimation}
                  disabled={loading || totalEstimation === 0}
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                  sx={{
                    background: 'linear-gradient(45deg, #4caf50, #66bb6a)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #43a047, #5cb85c)',
                    }
                  }}
                >
                  {loading ? 'Saving...' : 'Save Estimation'}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Section 3: Profit Calculation */}
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <Card>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  <Analytics sx={{ mr: 1 }} />
                  Profit Calculation
                </Typography>

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Select Saved Estimation</InputLabel>
                  <Select
                    value={selectedSavedEstimationId}
                    onChange={(e) => setSelectedSavedEstimationId(e.target.value)}
                    label="Select Saved Estimation"
                  >
                    {savedCostEstimations.map((est) => (
                      <MenuItem key={est.id} value={est.id}>
                        Estimation ID: {est.id} | Total: PKR {est.total_estimation} | 
                        Date: {new Date(est.timestamp).toLocaleDateString()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedSavedEstimationId !== '' && (
                  <TextField
                    fullWidth
                    type="number"
                    label="Amount Taken"
                    value={amountTaken}
                    onChange={(e) => setAmountTaken(parseFloat(e.target.value) || 0)}
                    sx={{ mb: 3 }}
                    InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                  />
                )}

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSaveAnalytics}
                  disabled={loading || selectedSavedEstimationId === ''}
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                >
                  {loading ? 'Saving...' : 'Save Result'}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Section 4: Saved Estimations Table */}
        <Grid item xs={12}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.5 }}
          >
            <Card>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  Saved Estimations
                </Typography>

                {savedCostEstimations.length > 0 ? (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>ID</strong></TableCell>
                          <TableCell><strong>Menu</strong></TableCell>
                          <TableCell><strong>Guests</strong></TableCell>
                          <TableCell><strong>Location</strong></TableCell>
                          <TableCell><strong>Total Estimation</strong></TableCell>
                          <TableCell><strong>Amount Taken</strong></TableCell>
                          <TableCell><strong>Profit</strong></TableCell>
                          <TableCell><strong>Date</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {savedCostEstimations.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>{item.id}</TableCell>
                            <TableCell>{item.prediction_menu}</TableCell>
                            <TableCell>{item.prediction_guests}</TableCell>
                            <TableCell>{item.prediction_location}</TableCell>
                            <TableCell>PKR {item.total_estimation.toLocaleString()}</TableCell>
                            <TableCell>PKR {item.amount_taken?.toLocaleString() || 0}</TableCell>
                            <TableCell sx={{ color: (item.amount_taken - item.total_estimation) >= 0 ? 'green' : 'red' }}>
                              PKR {(item.amount_taken - item.total_estimation).toLocaleString()}
                            </TableCell>
                            <TableCell>{new Date(item.timestamp).toLocaleDateString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary">No estimations saved yet.</Typography>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Section 5: Profit Chart */}
        {profitChartData && (
          <Grid item xs={12}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0, duration: 0.5 }}
            >
              <Card>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                    Profit Over Time
                  </Typography>
                  <Line data={profitChartData} />
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        )}
      </Grid>

      {/* Price Update Modal */}
      <Dialog open={isModalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>Update Price for {selectedProduct}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="number"
            label="Enter New Price (PKR)"
            value={currentPrice}
            onChange={(e) => setCurrentPrice(e.target.value)}
            sx={{ mt: 2 }}
            InputProps={{ inputProps: { min: 0, step: 0.01 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button onClick={handleUpdatePrice} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CostCalculation; 